import { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import React from 'react';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import useBackendApi from '../hooks/useBackendApi';
import { RequestOptions } from '../types/httpRequests';
import PrimaryBlueButton from '../components/common/buttons/PrimaryBlueButton';

const backendURL = process.env.REACT_APP_BACKEND_URL;

const UserHipaaNoticeAcknowledgement = () => {
  const { theme } = useThemeContext();
  const [searchParams] = useSearchParams();
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'already_confirmed' | 'error' | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);

  const { apiFetchRequest } = useBackendApi();

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) setToken(t);
  }, [searchParams]);

  const handleConfirmAck = async () => {
    setAcknowledging(true);

    const endpointUrl: string = `${backendURL}/users/acknowledge-hipaa`;
    const apiRequestOptions: RequestOptions = {
      body: { token },
      method: 'POST',
      endpoint: endpointUrl,
    };

    try {
      const response = await apiFetchRequest(apiRequestOptions);
      if (response.status === 200) {
        setSubmitStatus('success');
      } else if (response.status === 409) {
        setSubmitStatus('already_confirmed');
      }
    } catch (error: any) {
      setSubmitStatus('error');
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 3,
        padding: { xs: '0 24px', sm: '0 24px', md: '0' },
        fontFamily: 'Arial',
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          color: theme.custom.components.userHipaaNoticeAck.typography.color,
        }}
      >
        HIPAA Notice Acknowledgment
      </Typography>

      <Typography
        variant="body1"
        sx={{ mb: 2, color: theme.custom.components.userHipaaNoticeAck.typography.body1.color }}
      >
        Please confirm that you've received and reviewed your HIPAA Privacy Notice. It was sent to your registered email
        address.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} color="primary" />
        }
        label="I acknowledge receipt of the HIPAA Privacy Notice."
        sx={{ color: theme.custom.components.userHipaaNoticeAck.typography.color }}
      />

      <Box sx={{ mt: 2 }}>
        <PrimaryBlueButton
          isLoading={acknowledging}
          onClick={handleConfirmAck}
          buttonText="Confirm Acknowledgment"
          sxProps={{
            height: '42px',
            marginLeft: { xs: '0', sm: '10px' },
          }}
          additionalProps={{
            disabled: !acknowledged || !token || acknowledging,
          }}
        />
      </Box>

      {submitStatus === 'success' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Thank you. Your acknowledgment has been recorded.
        </Alert>
      )}

      {submitStatus === 'already_confirmed' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This notice has already been acknowledged or the link is no longer valid.
        </Alert>
      )}

      {submitStatus === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to process acknowledgment. Please try again later.
        </Alert>
      )}
    </Box>
  );
};

export default UserHipaaNoticeAcknowledgement;
